import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuGroup,
  SqDropdownMenuItem,
  SqDropdownMenuTrigger,
} from "@/components/squircle-ui/dropdown";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CreativeType, PlatformType } from "@/types";
import {
  RiAddLine,
  RiArrowDownSLine,
  RiInformationLine,
  RiSearchLine,
} from "@remixicon/react";
import { useEffect, useRef, useState } from "react";
import MediaIcon from "@/components/common/MediaIcon";
import { SqButton } from "@/components/squircle-ui/button";
import LabelActions from "@/screens/creative-label/components/LabelActions";
import { cn } from "@/lib/utils";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { searchSort } from "@/utils/searchSort";
export type GroupByOption = {
  key: string;
  label: string;
  type?: string;
  creative_type?: CreativeType;
  show_platforms?: boolean;
  platforms?: PlatformType[];
  info?: string;
  supported_breakdowns?: string[];
};
interface Props {
  options: GroupByOption[];
  value: string;
  accountId: string;
  sectionId: string;
  onChange?: (value: string) => void;
  handleLabelDelete?: (labelId: string) => Promise<void>;
  handleCreateLabel?: () => void;
}
const GroupBy = ({
  options,
  value,
  onChange,
  handleLabelDelete,
  accountId,
  sectionId,
  handleCreateLabel,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [isBottomScrollable, setIsBottomScrollable] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  const checkScrollable = (isCurrentlyOpen: boolean) => {
    if (!isCurrentlyOpen || !optionsContainerRef.current) {
      setIsBottomScrollable(false);
      return;
    }

    const container = optionsContainerRef.current;
    const { scrollHeight, clientHeight, scrollTop } = container;

    const hasContent = scrollHeight > 0;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;

    setIsBottomScrollable(hasContent && isScrollable && !isAtBottom);
  };

  const handleCreateLabelClick = () => {
    handleCreateLabel?.();
    setIsOpen(false);
  };

  const selectedOption = options.find((o) => o.key === value);
  // Separate options into Meta Data and AI Label groups
  const metaDataOptions = options.filter((option) => option?.type !== "text_ai_label");
  const aiLabelOptions = options.filter((option) => option?.type === "text_ai_label");
  const isAiLabelSelected = aiLabelOptions.find((option) => option?.key === value);
  const isMetaDataSelected = metaDataOptions.find((option) => option?.key === value);

  // Sort Meta Data alphabetically
  const sortMetaData = (a: GroupByOption, b: GroupByOption) => {
    if (isMetaDataSelected) {
      if (a.key === isMetaDataSelected.key) return -1;
      if (b.key === isMetaDataSelected.key) return 1;
    }

    return a.label.localeCompare(b.label);
  };

  // Sort AI Label so that if an AI label is selected, it appears at the top, otherwise by creative_type priority then alphabetically
  const sortAiLabel = (a: GroupByOption, b: GroupByOption) => {
    // If a is the selected option, put it at the top
    if (isAiLabelSelected) {
      if (a.key === isAiLabelSelected.key) return -1;
      if (b.key === isAiLabelSelected.key) return 1;
    }

    // Define priority for creative_type
    const getPriority = (option: GroupByOption) => {
      if (!option.creative_type) return 1; // no creative_type gets priority 1
      if (option.creative_type === "video") return 2;
      if (option.creative_type === "image") return 3;
      return 4; // any other types
    };

    const aPriority = getPriority(a);
    const bPriority = getPriority(b);

    // If creative_type priority is different, sort by priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same priority, sort alphabetically
    return a.label.localeCompare(b.label);
  };

  const sortedMetaDataOptions = metaDataOptions.sort(sortMetaData);
  const sortedAiLabelOptions = aiLabelOptions.sort(sortAiLabel);

  // Mark component as unmounted on cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // on close if ai-label is selected keep open only ai-label (if it has content)
    if (!isOpen && isAiLabelSelected && sortedAiLabelOptions.length > 0)
      setTimeout(() => {
        if (mountedRef.current) setOpenSections(["ai-label"]);
      }, 300);
    // on close if meta-data is selected keep open both accordion (if they have content)
    if (!isOpen && isMetaDataSelected) {
      const sections = ["meta-data"];
      if (sortedAiLabelOptions.length > 0) sections.push("ai-label");
      setTimeout(() => {
        if (mountedRef.current) setOpenSections(sections);
      }, 300);
    }
  }, [isOpen, isAiLabelSelected, isMetaDataSelected, sortedAiLabelOptions.length]);

  return (
    <SqDropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) requestAnimationFrame(() => checkScrollable(true));
        else setSearchTerm("");
      }}
    >
      <SqDropdownMenuTrigger>
        <div className="mr-2 flex items-center gap-2 overflow-hidden max-w-[272px] truncate">
          <span className="shrink-0 overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-medium leading-none tracking-[0.24px]">
            Group by
          </span>
          <div className="flex items-center gap-1">
            {selectedOption?.type === "text_ai_label" &&
              (selectedOption?.creative_type === "image" ||
                selectedOption?.creative_type === "video") && (
                <MediaIcon
                  type={selectedOption.creative_type}
                  variant="raw"
                  size={14}
                  className="shrink-0"
                />
              )}
            <span className="mt-[2px] flex-1 truncate text-brand-primary-600 text-[12px] font-normal leading-none tracking-[0.24px]">
              {selectedOption?.label}
            </span>
          </div>
        </div>
        <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
      </SqDropdownMenuTrigger>
      <SqDropdownMenuContent
        side="bottom"
        align="start"
        className="w-[200px]"
        radius={12}
        collisionPadding={10}
      >
        <div className="flex items-center flex-1 squircle squircle-fill-selected-gray h-7 squircle-smooth-[1] squircle-radius-[8px] px-2 mb-1 relative">
          <RiSearchLine size={14} color="#02185966" className="mr-[2px]" />
          <Input
            value={searchTerm}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              // Check after the search results update
              requestAnimationFrame(() => checkScrollable(isOpen));

              // If there's a search term, check which sections have matching results
              if (newSearchTerm) {
                const hasMetaDataMatches = sortedMetaDataOptions.some((option) =>
                  option.label.toLowerCase().includes(newSearchTerm.toLowerCase()),
                );
                const hasAiLabelMatches = sortedAiLabelOptions.some((option) =>
                  option.label.toLowerCase().includes(newSearchTerm.toLowerCase()),
                );

                // Update openSections based on where matches are found
                const newOpenSections = [];
                if (hasMetaDataMatches) newOpenSections.push("meta-data");
                if (hasAiLabelMatches) newOpenSections.push("ai-label");
                setOpenSections(newOpenSections);
              } else {
                // When search is cleared, restore default accordion state (only if sections have content)
                const defaultSections = [];
                if (sortedMetaDataOptions.length > 0) defaultSections.push("meta-data");
                if (sortedAiLabelOptions.length > 0) defaultSections.push("ai-label");
                setOpenSections(defaultSections);
              }
            }}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={"Search"}
            className="shadow-none border-0 outline-none ring-0 text-[12px] px-1 relative top-[1px] placeholder:text-[#02185966] text-brand-primary-new"
          />
        </div>
        <div className="relative">
          <div
            ref={optionsContainerRef}
            onScroll={() => checkScrollable(isOpen)}
            className="mb-1 overflow-y-auto max-h-[330px]"
          >
            <SqTooltipProvider>
              <Accordion
                type="multiple"
                value={openSections}
                onValueChange={(v: string[]) => {
                  // Filter out sections that don't have content
                  const filteredSections = v.filter((section) => {
                    if (section === "meta-data") return sortedMetaDataOptions.length > 0;
                    if (section === "ai-label") return sortedAiLabelOptions.length > 0;
                    return true;
                  });
                  setOpenSections(filteredSections);
                  // setting setIsBottomScrollable immediately for special cases
                  if (!filteredSections.length) {
                    setIsBottomScrollable(false);
                    return;
                  }
                  if (filteredSections.includes("ai-label")) {
                    setIsBottomScrollable(true);
                    return;
                  }
                  if (
                    filteredSections.length === 1 &&
                    filteredSections.includes("meta-data")
                  ) {
                    setIsBottomScrollable(false);
                    return;
                  }
                  setTimeout(() => {
                    if (mountedRef.current) checkScrollable(isOpen);
                  }, 300);
                }}
                className="w-full"
              >
                {/* Meta Data Section */}
                {sortedMetaDataOptions.filter((option) =>
                  option.label.toLowerCase().includes(searchTerm.toLowerCase()),
                ).length > 0 && (
                  <>
                    <AccordionItem value="meta-data" className="border-none">
                      <AccordionTrigger
                        className="px-2 py-[6px] text-xs font-medium [&[data-state=closed]>svg]:-rotate-90 [&[data-state=open]>svg]:rotate-0 [&[data-state=closed]]:text-[#021859CC] [&[data-state=open]]:text-[#02185966]"
                        chevronClassName="text-[#02185966]"
                      >
                        Meta Data
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <SqDropdownMenuGroup>
                          {searchSort(
                            sortedMetaDataOptions.filter((option) =>
                              option.label
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                            ),
                            searchTerm,
                            (o) => o.label,
                          ).map((option) => (
                            <SqDropdownMenuItem
                              key={option.key}
                              className={`text-[#021859CC] px-2 leading-[16px] flex items-center justify-between gap-2 cursor-pointer ${option.key === value ? "squircle-fill-selected-gray hover:squircle-fill-selected-gray" : ""}`}
                              onClick={() => onChange?.(option.key)}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <SqTooltip>
                                  <SqTooltipTrigger asChild>
                                    <span className="truncate min-w-0 flex-1">
                                      {option.label}
                                      {option.show_platforms && (
                                        <span className="text-[#FF4500]">*</span>
                                      )}
                                    </span>
                                  </SqTooltipTrigger>
                                  <SqTooltipContent portal={true}>
                                    <p>{option.label}</p>
                                  </SqTooltipContent>
                                </SqTooltip>
                                {option.info && (
                                  <SqTooltip>
                                    <SqTooltipTrigger>
                                      <RiInformationLine
                                        size={14}
                                        className="text-brand-primary-400 shrink-0"
                                      />
                                    </SqTooltipTrigger>
                                    <SqTooltipContent
                                      side="right"
                                      align="start"
                                      portal={true}
                                    >
                                      <p>{option.info}</p>
                                    </SqTooltipContent>
                                  </SqTooltip>
                                )}
                              </div>

                              {option.show_platforms &&
                                option?.platforms &&
                                option.platforms.length > 0 && (
                                  <SqTooltip>
                                    <SqTooltipTrigger asChild>
                                      <div className="flex items-center gap-1">
                                        {option.platforms.length <= 2 ? (
                                          // Show all icons if 2 or fewer platforms
                                          option.platforms?.map((p, index) => (
                                            <img
                                              key={index}
                                              className="h-[12px] w-[12px] flex-shrink-0"
                                              src={getPlatformIcon(p)}
                                            />
                                          ))
                                        ) : (
                                          // Show first icon + count for remaining
                                          <>
                                            <img
                                              className="h-[12px] w-[12px] flex-shrink-0"
                                              src={getPlatformIcon(option.platforms[0])}
                                            />
                                            <span className="translate-y-[1px] text-[#02185999]">
                                              +{option.platforms.length - 1}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </SqTooltipTrigger>

                                    <SqTooltipContent
                                      portal={true}
                                      side="top"
                                      align="end"
                                      alignOffset={-40}
                                    >
                                      <p>
                                        Available for{" "}
                                        {option.platforms
                                          .map(
                                            (platform) =>
                                              platform.charAt(0).toUpperCase() +
                                              platform.slice(1).toLowerCase(),
                                          )
                                          .join(", ")}
                                      </p>
                                    </SqTooltipContent>
                                  </SqTooltip>
                                )}
                            </SqDropdownMenuItem>
                          ))}
                        </SqDropdownMenuGroup>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                )}

                {/* AI Label Section */}
                {sortedAiLabelOptions.filter((option) =>
                  option.label.toLowerCase().includes(searchTerm.toLowerCase()),
                ).length > 0 && (
                  <>
                    {sortedMetaDataOptions.filter((option) =>
                      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
                    ).length > 0 && (
                      <div className="mx-[6px] my-[4px] h-[1px] rounded-full bg-[#EDEDED]" />
                    )}
                    <AccordionItem value="ai-label" className="border-none">
                      <AccordionTrigger
                        className="px-2 py-[6px] text-xs font-medium [&[data-state=closed]>svg]:-rotate-90 [&[data-state=open]>svg]:rotate-0 [&[data-state=closed]]:text-[#021859CC] [&[data-state=open]]:text-[#02185966]"
                        chevronClassName="text-[#02185966]"
                      >
                        AI Label
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <SqDropdownMenuGroup>
                          {searchSort(
                            sortedAiLabelOptions.filter((option) =>
                              option.label
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                            ),
                            searchTerm,
                            (o) => o.label,
                          ).map((option) => (
                            <SqDropdownMenuItem
                              key={option.key}
                              className={`text-[#021859CC] px-2 leading-[16px] flex items-center group cursor-pointer min-w-0 ${option.key === value ? "squircle-fill-selected-gray hover:squircle-fill-selected-gray" : ""}`}
                              onClick={() => {
                                setOpenSections(["ai-label"]);
                                onChange?.(option.key);
                              }}
                            >
                              <MediaIcon
                                type={option.creative_type ?? "mixed"}
                                variant="raw"
                                size={14}
                                className="mr-2 self-start translate-y-[1px] shrink-0"
                              />
                              <SqTooltip>
                                <SqTooltipTrigger asChild>
                                  <span className="truncate min-w-0 flex-1">
                                    {option.label}
                                  </span>
                                </SqTooltipTrigger>
                                <SqTooltipContent portal={true}>
                                  <p>{option.label}</p>
                                </SqTooltipContent>
                              </SqTooltip>
                              <LabelActions
                                type="label"
                                itemKey={option.key}
                                itemLabel={option.label}
                                accountId={accountId}
                                handleItemDelete={async () =>
                                  handleLabelDelete?.(option.key)
                                }
                                sectionId={sectionId}
                                setParentOpen={setIsOpen}
                              />
                            </SqDropdownMenuItem>
                          ))}
                        </SqDropdownMenuGroup>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                )}
              </Accordion>
            </SqTooltipProvider>
          </div>
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 h-[50px] pointer-events-none transition-opacity duration-200 rounded-[12px]",
              isBottomScrollable ? "opacity-100" : "opacity-0",
            )}
            style={{
              background:
                "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        </div>
        <div className="relative">
          <SqButton
            size={"sm"}
            className="w-full justify-start"
            bgColor="#FF45000F"
            hoverBgColor="#FF45001F"
            onClick={handleCreateLabelClick}
          >
            <RiAddLine size={14} className="shrink-0" color="#FF4500" />
            <span className="text-[#FF4500] font-normal not-italic text-[12px] leading-[20px] tracking-[0%] translate-y-[1px]">
              Create Label
            </span>
          </SqButton>
        </div>
      </SqDropdownMenuContent>
    </SqDropdownMenu>
  );
};

export default GroupBy;
